import React from 'react';

/**
 * Catches React render errors and shows a fallback UI instead of a blank screen.
 * Wrap the app or key route trees for production-grade error handling.
 * Optional: pass onError={(error, errorInfo) => {...}} to report to a service (e.g. Sentry).
 */
export default class ErrorBoundary extends React.Component {
    state = { hasError: false, error: null };

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        const { onError } = this.props;
        if (typeof onError === 'function') {
            try {
                onError(error, errorInfo);
            } catch (e) {
                if (typeof window !== 'undefined' && window.console) {
                    console.error('ErrorBoundary onError failed:', e);
                }
            }
        }
        if (typeof window !== 'undefined' && window.console && import.meta.env?.DEV) {
            console.error('ErrorBoundary caught:', error, errorInfo?.componentStack);
        }
    }

    render() {
        if (this.state.hasError) {
            const { fallback: Fallback } = this.props;
            if (Fallback) return <Fallback error={this.state.error} />;
            return (
                <div
                    style={{
                        padding: '2rem',
                        textAlign: 'center',
                        fontFamily: 'var(--font-body), sans-serif',
                        color: 'var(--text-primary, #333)',
                        maxWidth: 480,
                        margin: '2rem auto',
                    }}
                >
                    <h2 style={{ marginBottom: '0.5rem' }}>Something went wrong</h2>
                    <p style={{ color: 'var(--text-muted, #666)', marginBottom: '1rem' }}>
                        We’ve been notified. Try refreshing the page.
                    </p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '0.5rem 1rem',
                            borderRadius: 8,
                            border: '1px solid var(--border, #ddd)',
                            background: 'var(--bg-secondary, #f5f5f0)',
                            cursor: 'pointer',
                            fontSize: 14,
                        }}
                    >
                        Refresh page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}
